import { DetailedDiff } from 'deep-object-diff';

// Local copy of smart-apply-common src/fidelity/diffMessage.ts. `any` matches that source.
/* eslint-disable @typescript-eslint/no-explicit-any */

const MAX_REDACTED_MISMATCHES = 30;

const isSimpleType = (val: any): boolean => {
    if (val === null) {
        return true;
    }

    return typeof val !== 'object';
};

const richObjectToString = (val: any) => {
    if (
        !!val &&
        typeof val === 'object' &&
        val.toString !== Object.prototype.toString &&
        val.toString !== Array.prototype.toString
    ) {
        return val.toString();
    }

    return val;
};

/** Type/length only — never the jobseeker's answer text, filenames, or other PII. */
const describeRedactedValue = (val: unknown): string => {
    if (val === null) return 'null';
    if (val === undefined) return 'undefined';
    if (typeof val === 'string') return `string:${val.length}`;
    if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
        return typeof val;
    }
    if (Array.isArray(val)) return `array:${val.length}`;
    if (typeof val === 'object') return 'object';
    return typeof val;
};

const formatLeaf = (val: any, redactValues: boolean): string =>
    redactValues ? describeRedactedValue(val) : richObjectToString(val);

const generateMismatches = ({
    getActualApplicationValFn,
    getTransformedApplicationValFn,
    diffObj,
    diffType,
    keyPrefix = '',
    depth = 0,
    redactValues = false,
}: {
    getActualApplicationValFn: (key: string) => any;
    getTransformedApplicationValFn: (key: string) => any;
    diffObj: Record<string, any>;
    diffType: string;
    keyPrefix?: string;
    depth?: number;
    redactValues?: boolean;
}): string[] => {
    if (depth >= 10) {
        const summary = redactValues
            ? 'redacted'
            : JSON.stringify(diffObj, (_, v: any) => (v === undefined ? null : v));
        return [`deepDifference${diffType}[${keyPrefix}]: (${summary})`];
    }

    const mismatches: string[] = [];

    Object.keys(diffObj).forEach((key) => {
        const actualApplicationVal = getActualApplicationValFn(key);
        const transformedApplicationVal = getTransformedApplicationValFn(key);

        const isSimpleActualAppVal = isSimpleType(richObjectToString(actualApplicationVal));
        const isSimpleTransformedAppVal = isSimpleType(
            richObjectToString(transformedApplicationVal),
        );
        const nestedDiff = diffObj[key];
        // smart-apply-common recurses whenever either side is not simple. `deletedDiff` stores a
        // missing key as `undefined`; our answers are arrays, so that recurse would throw.
        if (
            (isSimpleActualAppVal && isSimpleTransformedAppVal) ||
            nestedDiff == null ||
            typeof nestedDiff !== 'object'
        ) {
            mismatches.push(
                `${keyPrefix}${key}: (${formatLeaf(actualApplicationVal, redactValues)}|${formatLeaf(transformedApplicationVal, redactValues)})`,
            );
        } else {
            const getNewActualApplicationValFn = isSimpleActualAppVal
                ? () => null
                : (newKey: string) => getActualApplicationValFn(key)[newKey];
            const getNewTransformedApplicationValFn = isSimpleTransformedAppVal
                ? () => null
                : (newKey: string) => getTransformedApplicationValFn(key)[newKey];

            mismatches.push(
                ...generateMismatches({
                    getActualApplicationValFn: getNewActualApplicationValFn,
                    getTransformedApplicationValFn: getNewTransformedApplicationValFn,
                    diffObj: nestedDiff,
                    diffType,
                    keyPrefix: `${keyPrefix}${key}.`,
                    depth: depth + 1,
                    redactValues,
                }),
            );
        }
    });

    return mismatches;
};

export const getDiffMessage = (
    actualApplication: Record<string, any>,
    transformedApplicationData: Record<string, any>,
    diff: DetailedDiff,
    options?: { redactValues?: boolean },
) => {
    const redactValues = options?.redactValues === true;
    const mismatches: string[] = [];

    mismatches.push(
        ...generateMismatches({
            getActualApplicationValFn: () => null,
            getTransformedApplicationValFn: (key) => transformedApplicationData[key],
            diffObj: diff.added,
            diffType: 'Added',
            redactValues,
        }),
    );
    mismatches.push(
        ...generateMismatches({
            getActualApplicationValFn: (key) => actualApplication[key],
            getTransformedApplicationValFn: () => null,
            diffObj: diff.deleted,
            diffType: 'Deleted',
            redactValues,
        }),
    );
    mismatches.push(
        ...generateMismatches({
            getActualApplicationValFn: (key) => actualApplication[key],
            getTransformedApplicationValFn: (key) => transformedApplicationData[key],
            diffObj: diff.updated,
            diffType: 'Updated',
            redactValues,
        }),
    );

    mismatches.sort();
    if (redactValues && mismatches.length > MAX_REDACTED_MISMATCHES) {
        const extra = mismatches.length - MAX_REDACTED_MISMATCHES;
        return `mismatches: ['${mismatches.slice(0, MAX_REDACTED_MISMATCHES).join("', '")}', '+${extra} more']`;
    }

    return `mismatches: ['${mismatches.join("', '")}']`;
};
